import React, {
  createContext,
  FC,
  ReactNode,
  useMemo,
  useContext,
} from "react";
import { ConfigContext } from "@app/provider";
import {
  getSession as getSessionFromStorage,
  isActiveSession as checkActiveSession,
  updateSessionActivity as updateSessionActivityInStorage,
  clearStorage,
  type PippaSession,
} from "../helpers/localStorageHelper";

const DEFAULT_SESSION_TTL_MINUTES = 1440;

export interface SessionController {
  getSession: () => PippaSession | null;
  isActiveSession: (session: PippaSession | null) => boolean;
  getSessionTtlMs: () => number;
  updateSessionActivity: (chatId: string) => void;
  clearStorage: () => void;
}

export const Context = createContext<SessionController>({
  getSession: () => null,
  isActiveSession: () => false,
  getSessionTtlMs: () => 0,
  updateSessionActivity: () => undefined,
  clearStorage: () => undefined,
});

Context.displayName = "PippaSessionContext";

const SessionProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { config } = useContext(ConfigContext);

  const sessionTtlMinutes =
    config?.pippaConfig?.sessionTtlMinutes ?? DEFAULT_SESSION_TTL_MINUTES;

  const ttlMs = sessionTtlMinutes * 60 * 1000;

  const value = useMemo<SessionController>(
    () => ({
      getSession: getSessionFromStorage,
      isActiveSession: (session) => checkActiveSession(session, ttlMs),
      getSessionTtlMs: () => ttlMs,
      updateSessionActivity: updateSessionActivityInStorage,
      clearStorage,
    }),
    [ttlMs]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default SessionProvider;
